import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from "@tanstack/react-query";
import {
  GET_CATEGORY_RANKING_FOR_UTAGE_WEB,
  GET_FOOTPRINT_HISTORY_FOR_WEB,
  GET_RECENT_VIDEO_CALL_FEMALE_USERS,
  GET_USERS_BY_PREFERENCE,
  GET_USERS_BY_PREFERENCE_EXCLUDING_LONG_CALL,
  UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
} from "~/constants/apiEndpoints";
import { APPLICATION_ID } from "~/constants/app";
import { apiClient } from "~/lib/apiClient";
import { queryKeys } from "~/lib/queryKeys";
import {
  fetchHighReviewScoreUsers,
  fetchPersonalityUsers,
  RECOMMEND_RANKING_TYPE,
  toRankedDiscoverUser,
} from "~/lib/queryOptions/discoverSections";
import { buildSearchRequestBody } from "~/lib/queryOptions/searchUsers";
import type { CarinaDiscoverFilters } from "~/stores/useCarinaDiscoverFilterStore";
import type {
  FootprintListResponse,
  FootprintUser,
} from "~/types/footprintUser";
import type { RecentVideoCallUsersResponse } from "~/types/recentVideoCallUser";
import type {
  DiscoverListFilter,
  DiscoverSeeMoreSection,
  DiscoverSeeMoreUser,
  DiscoverUserListResponse,
  PreferenceMatchUsersResponse,
  RecommendRankingResponse,
} from "./types";

const PAGE_SIZE = 19;

/** さがす一覧のページ位置。`skip` は表示ログの通し順位に使う */
type DiscoverPageParam = {
  /** 次ページの起点 (last_login_time) */
  cursor?: string;
  /** そこまでに読み込み済みの件数 */
  skip: number;
};

const buildRequestBody = (
  { cursor, skip }: DiscoverPageParam,
  filter?: DiscoverListFilter,
) => {
  return {
    limit: PAGE_SIZE,
    sort_type: 0,
    default_avatar_flag: false,
    skip,
    ...(cursor !== undefined ? { last_login_time: cursor } : {}),
    ...filter,
  };
};

// filter 無しのキーは従来どおり list() のまま（route loader・既存アプリが依存する）
export const discoverUsersInfiniteQueryOptions = (
  filter?: DiscoverListFilter,
) =>
  infiniteQueryOptions({
    queryKey:
      filter === undefined
        ? queryKeys.discover.list()
        : queryKeys.discover.filteredList(filter),
    queryFn: ({ pageParam }) =>
      apiClient<DiscoverUserListResponse>(
        UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
        buildRequestBody(pageParam, filter),
      ),
    initialPageParam: { skip: 0 } as DiscoverPageParam,
    // 1 ページが PAGE_SIZE 未満で返ることがある（サーバーがビデオ通話チャンネラーを除外する）。
    // 終端はカーソルの有無で判定し、skip は実際に受け取った件数を積む
    getNextPageParam: (lastPage, allPages) => {
      const cursor = lastPage.at(-1)?.last_login_time;
      return cursor === undefined
        ? undefined
        : { cursor, skip: allPages.reduce((n, page) => n + page.length, 0) };
    },
  });

/**
 * 「近くに住んでる」をエリアと重ねる。両方指定ならエリア内に自分の居住地があるときだけ残す（無ければ 0 件）。
 * 戻り値 [] は「該当なし」で、API には投げない（region:[] は絞り込み無しに解釈されうるため）
 */
export const resolveCarinaSearchRegion = (
  areaRegion: number[] | undefined,
  nearbyRegion: number | null,
): number[] | undefined => {
  if (nearbyRegion === null) return areaRegion;
  if (areaRegion === undefined) return [nearbyRegion];
  return areaRegion.includes(nearbyRegion) ? [nearbyRegion] : [];
};

/** Carina のさがす画面で条件を指定したときの一覧。検索画面と同じ API・同じ条件の変換で最大 200 件取る */
export const carinaDiscoverSearchQueryOptions = (
  filters: CarinaDiscoverFilters,
  nearbyRegion: number | null,
) => {
  const { nearby, ...searchFilters } = filters;
  const region = nearby ? nearbyRegion : null;
  return queryOptions({
    queryKey: queryKeys.discover.carinaSearch(searchFilters, region),
    queryFn: async () => {
      const body = buildSearchRequestBody(searchFilters);
      const resolved = resolveCarinaSearchRegion(
        body.region as number[] | undefined,
        region,
      );
      if (resolved?.length === 0) return [];
      return apiClient<DiscoverUserListResponse>(
        UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
        resolved === undefined ? body : { ...body, region: resolved },
      );
    },
    // チップを押すたびに一覧がスケルトンへ戻らないよう、取り直しの間は前の結果を出しておく
    placeholderData: keepPreviousData,
  });
};

const SEE_MORE_PAGE_SIZE = 20;

/** 「もっと見る」一覧のページ位置（section ごとに使う項目が異なる） */
export type SeeMorePageParam = {
  /** 今すぐ通話用の 1 始まりのページ番号 */
  page: number;
  /** そこまでに読み込み済みの件数（offset / skip） */
  skip: number;
};

// 今すぐ通話のネストしたレスポンスを一覧カードが扱うフラット形へ変換する
const toDiscoverUserFromRecent = (
  item: RecentVideoCallUsersResponse[number],
): DiscoverSeeMoreUser => ({
  user_id: item.user_id,
  ava_id: item.user.ava_id,
  abt: item.user.abt,
  age: item.user.age,
  user_name: item.user.user_name,
  region: item.user.region,
  voice_call_waiting: item.user.voice_call_waiting,
  video_call_waiting: item.user.video_call_waiting,
  is_new_user: item.is_new,
  is_calling: false,
  is_live_now: false,
  last_login_time: item.user.last_login_time,
  has_story_movie: item.has_story_movie,
  step_to_call: item.user.step_to_call,
  has_lovense: item.has_lovense,
  is_listed_on_flea_market: false,
  bust_size: item.user.bust_size ?? undefined,
  average_score: item.user.average_score,
  review_count: item.user.review_count,
});

// 足あとは項目が少ないため、一覧カードが扱う形に不足分を既定値で補う
// （足あと API は last_login で返すので last_login_time へ詰め替える。オンライン判定に使う）
const toDiscoverUserFromFootprint = (
  item: FootprintUser,
): DiscoverSeeMoreUser => ({
  user_id: item.user_id,
  ava_id: item.ava_id,
  abt: "",
  age: item.age ?? 0,
  user_name: item.user_name,
  region: item.region ?? 0,
  voice_call_waiting: item.voice_call_waiting ?? false,
  video_call_waiting: item.video_call_waiting ?? false,
  is_new_user: false,
  is_calling: false,
  is_live_now: false,
  last_login_time: item.last_login ?? "",
  has_story_movie: false,
  step_to_call: 0,
  has_lovense: false,
  is_listed_on_flea_market: false,
});

const fetchSeeMorePage = async (
  section: DiscoverSeeMoreSection,
  { page, skip }: SeeMorePageParam,
  excludeLongCall: boolean,
): Promise<DiscoverSeeMoreUser[]> => {
  switch (section) {
    case "instant_call": {
      const items = await apiClient<RecentVideoCallUsersResponse>(
        GET_RECENT_VIDEO_CALL_FEMALE_USERS,
        { application_id: APPLICATION_ID, limit: SEE_MORE_PAGE_SIZE, page },
      );
      return items.map(toDiscoverUserFromRecent);
    }
    case "high_review":
      return fetchHighReviewScoreUsers(SEE_MORE_PAGE_SIZE, skip);
    case "preference_match": {
      const users = await apiClient<PreferenceMatchUsersResponse>(
        excludeLongCall
          ? GET_USERS_BY_PREFERENCE_EXCLUDING_LONG_CALL
          : GET_USERS_BY_PREFERENCE,
        { skip, take: SEE_MORE_PAGE_SIZE },
      );
      return users.map((u) => ({
        ...u,
        last_login_time: u.last_login_time || u.last_login || "",
      }));
    }
    case "sadistic":
    case "masochistic":
      return fetchPersonalityUsers(section, skip, SEE_MORE_PAGE_SIZE);
    case "footprint": {
      const items = await apiClient<FootprintListResponse>(
        GET_FOOTPRINT_HISTORY_FOR_WEB,
        { skip, take: SEE_MORE_PAGE_SIZE },
      );
      return items.map(toDiscoverUserFromFootprint);
    }
    case "ranking": {
      const data = await apiClient<RecommendRankingResponse>(
        GET_CATEGORY_RANKING_FOR_UTAGE_WEB,
        { type: RECOMMEND_RANKING_TYPE, skip, take: SEE_MORE_PAGE_SIZE },
      );
      // BE は 100 位までしか返さず、skip>=100 では data 無しで返る
      return (data ?? []).map(toRankedDiscoverUser);
    }
  }
};

// 件数ぴったりで返る API は limit 未満で終端。
// 今すぐ通話は取得後に除外するため短いページがありうるので空で終端する
const hasNextSeeMorePage = (
  section: DiscoverSeeMoreSection,
  lastPage: DiscoverSeeMoreUser[],
) =>
  section === "instant_call"
    ? lastPage.length > 0
    : lastPage.length >= SEE_MORE_PAGE_SIZE;

// 「もっと見る」一覧用。さがす画面では使わず、一覧ページでのみ取得する
//
// excludeLongCall: 好みに合う子のみ有効。3分以上通話した女性を除いた版を使う（キャッシュキーも分ける）
export const discoverSeeMoreInfiniteQueryOptions = (
  section: DiscoverSeeMoreSection,
  excludeLongCall = false,
) =>
  infiniteQueryOptions({
    queryKey: excludeLongCall
      ? queryKeys.discover.seeMoreExcludingLongCall(section)
      : queryKeys.discover.seeMore(section),
    queryFn: ({ pageParam }) =>
      fetchSeeMorePage(section, pageParam, excludeLongCall),
    initialPageParam: { page: 1, skip: 0 } as SeeMorePageParam,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (!hasNextSeeMorePage(section, lastPage)) return undefined;
      return {
        page: lastPageParam.page + 1,
        skip: allPages.reduce((n, p) => n + p.length, 0),
      };
    },
  });

// おすすめ画面とも共有するため lib/queryOptions へ移動済み
export {
  cocoplusHighReviewScoreUsersQueryOptions,
  highReviewScoreUsersQueryOptions,
  newcomerUsersQueryOptions,
  preferenceMatchUsersQueryOptions,
  recommendRankingQueryOptions,
} from "~/lib/queryOptions/discoverSections";
